import React, { useState, useEffect } from 'react';
import { Interface } from '../../types/productIntegration';

interface InterfaceFormProps {
  interface?: Interface; // For editing existing interface
  onSave: (interfaceData: Omit<Interface, 'id'> | Interface) => void;
  onCancel: () => void;
  isOpen: boolean;
}

const InterfaceForm: React.FC<InterfaceFormProps> = ({ interface: interfaceData, onSave, onCancel, isOpen }) => {
  const [mode, setMode] = useState<'user' | 'dev'>('user');
  const [showModeTooltip, setShowModeTooltip] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiFiles, setAiFiles] = useState<FileList | null>(null);
  const [aiRequirementsUrl, setAiRequirementsUrl] = useState('');
  
  const [formData, setFormData] = useState<Omit<Interface, 'id'>>({
    name: '',
    direction: 'bidirectional',
    status: 'active',
    protocol: '',
    description: '',
    authentication: '',
    dataFormat: '',
    technicalDocumentationUrl: '',
    categories: [],
    type: '',
    team: '',
    visibility: 'internal'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categoryInput, setCategoryInput] = useState('');

  useEffect(() => {
    if (interfaceData) {
      // Editing existing interface
      setFormData({
        name: interfaceData.name,
        direction: interfaceData.direction,
        status: interfaceData.status,
        protocol: interfaceData.protocol || '',
        description: interfaceData.description || '',
        authentication: interfaceData.authentication || '',
        dataFormat: interfaceData.dataFormat || '',
        technicalDocumentationUrl: interfaceData.technicalDocumentationUrl || '',
        categories: interfaceData.categories || [],
        type: interfaceData.type || '',
        team: interfaceData.team || '',
        visibility: interfaceData.visibility || 'internal'
      });
    } else {
      // Reset form for new interface
      setFormData({
        name: '',
        direction: 'bidirectional',
        status: 'active',
        protocol: '',
        description: '',
        authentication: '',
        dataFormat: '',
        technicalDocumentationUrl: '',
        categories: [],
        type: '',
        team: '',
        visibility: 'internal'
      });
    }
    setErrors({});
    setCategoryInput('');
  }, [interfaceData, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const addCategory = () => {
    const categories = formData.categories || [];
    if (categoryInput.trim() && !categories.includes(categoryInput.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...(prev.categories || []), categoryInput.trim()]
      }));
      setCategoryInput('');
    }
  };

  const removeCategory = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: (prev.categories || []).filter(c => c !== category)
    }));
  };

  const handleMappingToolClick = () => {
    window.open('https://oneinterface-transformation-mapping-tool-v2.dev.commandcentral.com/generate-template', '_blank');
  };

  const handleWorkflowBuilderClick = () => {
    window.open('#', '_blank'); // Generic workflow builder for demo
  };

  const handleCCAdminClick = () => {
    window.open('https://admin2.dev.commandcentral.com/landing-page', '_blank');
  };

  const handleAIAssistantClick = () => {
    setShowAIModal(true);
  };

  const handleDownloadCSIConfig = () => {
    // For demo purposes - simulate file download
    alert('CSI Config File Download (Demo)\n\nThis would download the generated CSI configuration file for Premier One integration.');
  };

  const handleDownloadXSLT = () => {
    // For demo purposes - simulate file download
    alert('XSLT File Download (Demo)\n\nThis would download the generated XSLT transformation file for Premier One integration.');
  };

  const handleGenerateDocumentation = () => {
    // For demo purposes - simulate documentation generation
    const interfaceType = formData.type || 'interface';
    alert(`Interface Documentation Generation (Demo)\n\nGenerating comprehensive documentation for ${formData.name || 'your interface'}...\n\nThis would create:\n• 📋 Interface Deployment Guide - Step-by-step implementation instructions\n• 🔧 Troubleshooting Manual - Common issues and solutions\n• 🧪 Test Plan & Validation Scripts - Automated testing procedures\n• 📊 Performance & Monitoring Guidelines\n\nDocumentation package will be available for download shortly.`);
  };

  const handleAIGenerate = () => {
    const isPremierOne = formData.type === 'premier-one-cad' || formData.type === 'premier-one-records';
    
    if (isPremierOne) {
      // For Premier One interfaces, mention the files that will be generated
      alert(`AI Interface Generation (Demo) - ${formData.type}\n\nAnalyzing requirements for ${formData.type} interface...\n\nThis would generate:\n• CSI Configuration File - Interface setup parameters\n• XSLT Transformation File - Data mapping rules\n\nFiles will be available for download after processing completes.`);
    } else {
      // For other interface types
      alert('AI Interface Generation (Demo)\n\nThis feature would analyze your requirements and generate interface specifications automatically. Coming soon!');
    }
    
    setShowAIModal(false);
    setAiPrompt('');
    setAiFiles(null);
    setAiRequirementsUrl('');
  };

  const handleAIModalClose = () => {
    setShowAIModal(false);
    setAiPrompt('');
    setAiFiles(null);
    setAiRequirementsUrl('');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Interface name is required';
    }
    if (!formData.protocol?.trim()) {
      newErrors.protocol = 'Protocol is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      if (interfaceData) {
        // Editing existing interface
        onSave({ ...formData, id: interfaceData.id } as Interface);
      } else {
        // Creating new interface
        onSave(formData);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content interface-form-modal">
        <div className="modal-header">
          <h3>{interfaceData ? 'Edit Interface' : 'Create New Interface'}</h3>
          <button className="close-modal" onClick={onCancel}>×</button>
        </div>
        
        {/* Mode Selector */}
        <div className="mode-selector">
          <div className="mode-toggle">
            <label className="toggle-label">
              <span className="mode-label user-mode">User mode</span>
              <input
                type="checkbox"
                checked={mode === 'dev'}
                onChange={(e) => setMode(e.target.checked ? 'dev' : 'user')}
              />
              <span className="slider"></span>
              <span className="mode-label dev-mode">Dev mode</span>
            </label>
            <button 
              className="mode-info-btn"
              onMouseEnter={() => setShowModeTooltip(true)}
              onMouseLeave={() => setShowModeTooltip(false)}
            >
              ℹ️
            </button>
            {showModeTooltip && (
              <div className="mode-tooltip">
                <div className="tooltip-content">
                  <strong>User Mode:</strong> For cataloging existing interfaces. Anyone with interface ecosystem knowledge can contribute.<br/><br/>
                  <strong>Dev Mode:</strong> For developers with advanced tools like mapping utilities and AI-assisted interface creation.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Dev Mode Tools */}
        {mode === 'dev' && (
          <div className="dev-tools">
            <div className="dev-tools-header">
              <h4>🛠️ Developer Tools</h4>
              {formData.type && (
                <span className="interface-type-badge">{formData.type}</span>
              )}
            </div>
            
            {/* Common Tools */}
            <div className="dev-actions">
              <button 
                type="button" 
                className="dev-tool-btn mapping-tool-btn"
                onClick={handleMappingToolClick}
              >
                🔗 Open Mapping Tool
              </button>
              <button 
                type="button" 
                className="dev-tool-btn ai-assistant-btn"
                onClick={handleAIAssistantClick}
              >
                ✨ AI Interface Assistant
              </button>
            </div>

            {/* Hybrid/Cloud Interface Tools */}
            {(formData.type === 'hybrid' || formData.type === 'cloud') && (
              <div className="specialized-tools">
                <h5>🌐 {formData.type === 'hybrid' ? 'Hybrid' : 'Cloud'} Interface Tools</h5>
                <div className="dev-actions">
                  <button 
                    type="button" 
                    className="dev-tool-btn workflow-builder-btn"
                    onClick={handleWorkflowBuilderClick}
                  >
                    🔄 Workflow Builder
                  </button>
                  <button 
                    type="button" 
                    className="dev-tool-btn cc-admin-btn"
                    onClick={handleCCAdminClick}
                  >
                    ⚙️ CC Admin Setup
                  </button>
                </div>
              </div>
            )}

            {/* Premier One Interface Tools */}
            {(formData.type === 'premier-one-cad' || formData.type === 'premier-one-records') && (
              <div className="specialized-tools">
                <h5>🏢 Premier One Tools ({formData.type === 'premier-one-cad' ? 'CAD' : 'Records'})</h5>
                <div className="premier-one-notice">
                  <p><strong>📋 Post-AI Processing:</strong> After AI analysis, you'll receive configuration files for implementation:</p>
                  <ul>
                    <li>CSI Configuration File - Interface setup parameters</li>
                    <li>XSLT Transformation File - Data mapping rules</li>
                  </ul>
                </div>
                <div className="dev-actions">
                  <button 
                    type="button" 
                    className="dev-tool-btn download-btn"
                    onClick={handleDownloadCSIConfig}
                    disabled={!showAIModal} // Enable after AI processing
                  >
                    📄 Download CSI Config
                  </button>
                  <button 
                    type="button" 
                    className="dev-tool-btn download-btn"
                    onClick={handleDownloadXSLT}
                    disabled={!showAIModal} // Enable after AI processing
                  >
                    📄 Download XSLT File
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="interface-form">
          <div className="form-group">
            <label htmlFor="name">Interface Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Enter interface name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="protocol">Protocol *</label>
              <input
                id="protocol"
                name="protocol"
                type="text"
                value={formData.protocol}
                onChange={handleInputChange}
                className={errors.protocol ? 'error' : ''}
                placeholder="e.g., REST API, GraphQL, SOAP"
              />
              {errors.protocol && <span className="error-message">{errors.protocol}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="direction">Direction</label>
              <select
                id="direction"
                name="direction"
                value={formData.direction}
                onChange={handleInputChange}
              >
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
                <option value="bidirectional">Bidirectional</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="planned">Planned</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="visibility">Visibility</label>
              <select
                id="visibility"
                name="visibility"
                value={formData.visibility}
                onChange={handleInputChange}
              >
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="private">Private</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Interface Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="">Select interface type</option>
                <option value="hybrid">Hybrid Interface</option>
                <option value="cloud">Cloud Interface</option>
                <option value="premier-one-cad">Premier One - CAD</option>
                <option value="premier-one-records">Premier One - Records</option>
                <option value="standard">Standard Interface</option>
                <option value="custom">Custom Interface</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="team">Responsible Team</label>
              <input
                id="team"
                name="team"
                type="text"
                value={formData.team}
                onChange={handleInputChange}
                placeholder="e.g., Integration Team, API Team"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="authentication">Authentication</label>
              <input
                id="authentication"
                name="authentication"
                type="text"
                value={formData.authentication}
                onChange={handleInputChange}
                placeholder="e.g., API Key, OAuth 2.0, Basic Auth"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dataFormat">Data Format</label>
              <input
                id="dataFormat"
                name="dataFormat"
                type="text"
                value={formData.dataFormat}
                onChange={handleInputChange}
                placeholder="e.g., JSON, XML, CSV"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Type</label>
              <input
                id="type"
                name="type"
                type="text"
                value={formData.type}
                onChange={handleInputChange}
                placeholder="e.g., API, Webhook, Message Queue"
              />
            </div>

            <div className="form-group">
              <label htmlFor="team">Team</label>
              <input
                id="team"
                name="team"
                type="text"
                value={formData.team}
                onChange={handleInputChange}
                placeholder="Responsible team"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="technicalDocumentationUrl">Documentation URL</label>
            <input
              id="technicalDocumentationUrl"
              name="technicalDocumentationUrl"
              type="url"
              value={formData.technicalDocumentationUrl}
              onChange={handleInputChange}
              placeholder="https://docs.example.com/api"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of the interface"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Categories</label>
            <div className="category-input-group">
              <input
                type="text"
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                placeholder="Add a category"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
              />
              <button type="button" onClick={addCategory} className="add-category-btn">
                Add
              </button>
            </div>
            {(formData.categories || []).length > 0 && (
              <div className="categories-list">
                {(formData.categories || []).map((category, index) => (
                  <span key={index} className="category-tag">
                    {category}
                    <button
                      type="button"
                      onClick={() => removeCategory(category)}
                      className="remove-category"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn">
              {interfaceData ? 'Update Interface' : 'Create Interface'}
            </button>
          </div>
        </form>
      </div>

      {/* AI Assistant Modal */}
      {showAIModal && (
        <div className="modal-overlay ai-modal-overlay">
          <div className="modal-content ai-assistant-modal">
            <div className="modal-header">
              <h3>✨ AI Interface Assistant
                {formData.type && (
                  <span className="ai-modal-type"> - {formData.type}</span>
                )}
              </h3>
              <button className="close-modal" onClick={handleAIModalClose}>×</button>
            </div>
            
            <div className="ai-assistant-content">
              <p className="ai-description">
                {(formData.type === 'premier-one-cad' || formData.type === 'premier-one-records') ? (
                  `Describe your ${formData.type} interface requirements. Our AI will analyze your specifications and generate both CSI configuration and XSLT transformation files for implementation.`
                ) : (
                  'Describe your interface requirements, upload specification files, or provide documentation links. Our AI will help generate the interface configuration automatically.'
                )}
                {mode === 'dev' && (
                  <><br/><br/><strong>Developer Tools:</strong> Generate comprehensive documentation including deployment guides, troubleshooting manuals, and test plans.</>
                )}
              </p>
              
              <div className="ai-form-group">
                <label htmlFor="ai-prompt">Describe your interface requirements:</label>
                <textarea
                  id="ai-prompt"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Example: I need a REST API interface for user authentication that accepts JSON, uses OAuth 2.0, and integrates with our internal user management system..."
                  rows={4}
                />
              </div>
              
              <div className="ai-form-group">
                <label htmlFor="ai-files">Upload specification files (optional):</label>
                <input
                  id="ai-files"
                  type="file"
                  multiple
                  accept=".json,.yaml,.yml,.xml,.txt,.pdf,.doc,.docx"
                  onChange={(e) => setAiFiles(e.target.files)}
                />
                <small>Supported: JSON, YAML, XML, TXT, PDF, DOC, DOCX</small>
              </div>
              
              <div className="ai-form-group">
                <label htmlFor="ai-requirements-url">Requirements/Documentation URL (optional):</label>
                <input
                  id="ai-requirements-url"
                  type="url"
                  value={aiRequirementsUrl}
                  onChange={(e) => setAiRequirementsUrl(e.target.value)}
                  placeholder="https://docs.example.com/api-requirements"
                />
              </div>
              
              <div className="ai-actions">
                <button type="button" onClick={handleAIModalClose} className="cancel-btn">
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={handleGenerateDocumentation} 
                  className="documentation-btn"
                  title="Generate comprehensive interface documentation"
                >
                  📋 Generate Documentation
                </button>
                <button 
                  type="button" 
                  onClick={handleAIGenerate} 
                  className="ai-generate-btn"
                  disabled={!aiPrompt.trim()}
                >
                  ✨ Generate Interface
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterfaceForm;
